import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getTenantToday } from '../utils/dateUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Database,
  Search,
  PlusCircle,
  RefreshCcw,
  Edit3,
  X,
  CheckCircle2,
  AlertCircle,
  Shield,
  ChevronRight,
  Tag,
  FileText,
  Link2,
  Droplets,
  History,
  Calendar,
  Award,
  Layers,
  Filter,
  ArrowUpDown,
  ChevronsUpDown,
  Activity,
  BookOpen,
  Ticket,
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { cn } from '../lib/utils';
import { leveragedLoanTicketPath, leveragedLoanTicketState, loan360Path } from '../utils/loanNavigation';

import { DateField } from './shared/DateField';

// ============== Types ==============
interface SecuritySummary {
  id: number;
  assetId: string;
  securityName: string;
  instrumentType: string;
  instrumentSubtype?: string;
  securityStatus: string;
  masterStatus?: string;
  cusip?: string;
  isin?: string;
  sedol?: string;
  ticker?: string;
  currency: string;
  couponRate?: number;
  maturityDate?: string;
  faceValue?: number;
  dayCountConvention?: string;
  couponFrequency?: string;
  quantityType?: string;
  issuerName?: string;
  issuerId?: number;
  assetClass?: string;
  subAssetClass?: string;
  assetSubClass?: string; // F-PUB-030c: free-text sub-class sent on create/update
  goldenSource?: string;
  updatedAt?: string;
  // Derivative fields
  underlyingSecurityId?: number;
  derivativeType?: string;
  expirationDate?: string;
  strikePrice?: number;
  optionType?: string;
  exerciseStyle?: string;
  notionalAmount?: number;
  // FX fields
  baseCurrency?: string;
  quoteCurrency?: string;
  settlementCurrency?: string;
  // Loan fields
  facilityType?: string;
  seniority?: string;
  baseRateIndex?: string;
  spread?: number;
  floor?: number;
  // Alternative fields
  vintageYear?: number;
  fundStrategy?: string;
  gpName?: string;
}

interface Identifier {
  id: number;
  identifierType: string;
  identifierValue: string;
  isPrimary: boolean;
  status: string;
  effectiveDate?: string;
  source?: string;
}

interface Rating {
  id: number;
  agency?: string;
  ratingAgency?: string;
  rating: string;
  outlook?: string;
  ratingOutlook?: string;
  effectiveDate: string;
  expirationDate?: string;
}

interface CouponSchedule {
  id: number;
  paymentDate: string;
  couponRate: number;
  amount?: number;
  status?: string;
}

interface PutCallSchedule {
  id: number;
  type: string;
  date: string;
  price: number;
  condition?: string;
}

interface AmortizationSchedule {
  id: number;
  date: string;
  principal: number;
  factor?: number;
  balance?: number;
}

interface RelatedSecurity {
  id: number;
  relatedSecurityId: number;
  relatedSecurityName: string;
  relationshipType: string;
  description?: string;
}

interface Enhancement {
  id: number;
  enhancementType: string;
  provider?: string;
  providerName?: string;
  amount?: number;
  coverage?: string;
  coveragePct?: number;
  coverageAmount?: number;
  expirationDate?: string;
  expiryDate?: string;
  status?: string;
}

interface LiquiditySnapshot {
  tier?: string;
  averageDailyVolume?: number;
  bidAskSpread?: number;
  venueAvailability?: string[];
  lastUpdated?: string;
}

interface AuditEntry {
  id: number;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  changedBy: string;
  changedAt: string;
}

interface Classification {
  id: number;
  scheme: string;
  category: string;
  subcategory?: string;
}

// Private credit detail surfaced on LOAN-instrument securities.
// Populated from GET /api/security-master/{id}/private-credit-detail.
interface PcLoanSummary {
  id: number;
  loanId?: string;
  loanName?: string;
  borrowerName?: string;
  borrowerTicker?: string;
  sponsorName?: string;
  facilityType?: string;
  seniority?: string;
  status?: string;
  originalCommitment?: number;
  currentCommitment?: number;
  fundedAmount?: number;
  unfundedAmount?: number;
  baseRate?: string;
  spreadBps?: number;
  csaBps?: number;
  floorRate?: number;
  allInRate?: number;
  stepDownSpreadBps?: number;
  stepDownTriggerLeverage?: number;
  oidPrice?: number;
  upfrontFeeBps?: number;
  closingDate?: string;
  maturityDate?: string;
  firstPaymentDate?: string;
  pikToggle?: string;
  pikElectionCurrent?: string;
  pikSpreadBps?: number;
  ddtlCommitment?: number;
  ddtlFunded?: number;
  ddtlUndrawn?: number;
  ddtlExpiry?: string;
  tickingFeeBps?: number;
  commitmentFeeBps?: number;
  spRating?: string;
  moodysRating?: string;
  fitchRating?: string;
  krollRating?: string;
  sector?: string;
  industry?: string;
  subIndustry?: string;
  nonAccrualStatus?: boolean;
  nonAccrualDate?: string;
  waterfallRecompute?: {
    baseRateLabel?: string;
    csaPct?: number;
    spreadPct?: number;
    floorPct?: number;
    storedAllInRate?: number;
    expression?: string;
  };
}

interface PcCovenantSummary {
  id: number;
  covenantName?: string;
  covenantType?: string;
  covenantCategory?: string;
  metricType?: string;
  thresholdType?: string;
  thresholdValue?: number;
  thresholdValueMax?: number;
  measurementFrequency?: string;
  measurementPeriod?: string;
  hasStepSchedule?: boolean;
  stepSchedule?: string;
  hasEquityCure?: boolean;
  maxEquityCures?: number;
  equityCureAmountCap?: number;
  effectiveDate?: string;
  isActive?: boolean;
}

interface PcLenderSummary {
  id: number;
  lenderName?: string;
  lenderRole?: string;
  commitmentAmount?: number;
  proRataPct?: number;
  facilityBreakdown?: string;
  isActive?: boolean;
}

interface PcDetailPayload {
  loan: PcLoanSummary | null;
  covenants: PcCovenantSummary[];
  lenderRegister: PcLenderSummary[];
  relatedSecurities?: RelatedSecurity[];
}

interface LlLoanSummary {
  id: number;
  loanId?: string;
  loanName?: string;
  borrowerName?: string;
  facilityType?: string;
  status?: string;
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// ============== Constants ==============
// Static fallback ordering -- the *visible* chip set is derived from the fetched
// data via `dynamicInstrumentTypes` (useMemo below), so chips reflect the
// asset classes the tenant actually holds.  Per polish backlog item #1:
// public-FI shops see BOND/DERIVATIVE/FX; PC shops see LOAN/FUND; nobody sees
// chips for instrument types they don't hold.
const STATUS_OPTIONS = ['ACTIVE', 'MATURED', 'DEFAULTED', 'CALLED', 'REDEEMED', 'SUSPENDED', 'PENDING'] as const;

const INSTRUMENT_COLORS: Record<string, string> = {
  BOND: '#3b82f6', LOAN: '#a855f7', DERIVATIVE: '#f97316',
  FX: '#06b6d4', FUND: '#8b5cf6', CASH: '#10b981',
};
// Preferred display order when types are present; anything else sorts alpha.
const INSTRUMENT_TYPE_ORDER = ['BOND', 'LOAN', 'DERIVATIVE', 'FX', 'FUND', 'CASH'];
const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#10b981', MATURED: '#64748b', DEFAULTED: '#ef4444',
  CALLED: '#f59e0b', REDEEMED: '#6366f1', SUSPENDED: '#f97316', PENDING: '#eab308',
};

const DETAIL_TABS = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'identifiers', label: 'Identifiers', icon: Tag },
  { id: 'ratings', label: 'Ratings', icon: Award },
  { id: 'schedules', label: 'Schedules', icon: Calendar },
  { id: 'relationships', label: 'Relationships', icon: Link2 },
  { id: 'enhancements', label: 'Enhancements', icon: Shield },
  { id: 'liquidity', label: 'Liquidity', icon: Droplets },
  { id: 'audit', label: 'Audit Trail', icon: History },
] as const;

type TabId = typeof DETAIL_TABS[number]['id'];

// ============== Styled Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 1.5rem 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const CustomScrollbar = styled.div`
  overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
  &::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
`;

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
};

// ============== Small Reusable Components ==============
const Badge: React.FC<{ label: string; colorMap: Record<string, string> }> = ({ label, colorMap }) => {
  const color = colorMap[label] || '#64748b';
  return (
    <span
      className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider"
      style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}
    >
      {label}
    </span>
  );
};

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => (
  <motion.div
    variants={fadeInUp}
    className="glass-card p-4 border-l-4 relative overflow-hidden group"
    style={{ borderLeftColor: color, background: 'rgba(30,41,59,0.5)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', borderLeft: `4px solid ${color}`, borderRadius: '16px' }}
  >
    <div className="flex justify-between items-start">
      <div>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
        <div className="text-xl font-bold text-white tracking-tight">{value}</div>
      </div>
      <div className="w-9 h-9 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
        {icon}
      </div>
    </div>
  </motion.div>
);

const FilterChip: React.FC<{ label: string; active: boolean; color?: string; onClick: () => void }> = ({ label, active, color, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'px-3 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all border',
      active
        ? 'text-white border-transparent'
        : 'text-slate-500 border-white/5 hover:border-white/10 hover:text-slate-300 bg-transparent'
    )}
    style={active ? { background: `${color || '#3b82f6'}30`, color: color || '#3b82f6', borderColor: `${color || '#3b82f6'}50` } : {}}
  >
    {label}
  </button>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 mt-5 first:mt-0">{children}</div>
);

const DetailField: React.FC<{ label: string; value?: string | number | null; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-0.5">{label}</div>
    <div className={cn('text-sm text-slate-200', mono && 'font-mono')}>{value ?? '--'}</div>
  </div>
);

const MiniTable: React.FC<{ columns: { key: string; label: string; width?: string; mono?: boolean }[]; rows: Record<string, any>[]; emptyText?: string }> = ({ columns, rows, emptyText }) => (
  <div className="border border-white/5 rounded-xl overflow-hidden">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-950/50">
          {columns.map(c => (
            <th key={c.key} className="px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest" style={{ width: c.width }}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-slate-600 text-xs">{emptyText || 'No data available'}</td></tr>
        ) : rows.map((row, i) => (
          <tr key={i} className="border-t border-white/[0.03] hover:bg-white/[0.02] transition-colors">
            {columns.map(c => (
              <td key={c.key} className={cn('px-4 py-2.5 text-slate-300', c.mono && 'font-mono')}>{row[c.key] ?? '--'}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FormField: React.FC<{
  label: string;
  children: React.ReactNode;
  span?: number;
}> = ({ label, children, span }) => (
  <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all placeholder:text-slate-700';
const selectCls = cn(inputCls, 'appearance-none');

// ============== Main Component ==============
const SecurityManagementScreen: React.FC = () => {
  const { canManageSecurityMaster, userRole } = useRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // -- Grid state --
  const [securities, setSecurities] = useState<SecuritySummary[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<string>('assetId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // -- Detail panel --
  const [selectedSecurity, setSelectedSecurity] = useState<SecuritySummary | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [detailLoading, setDetailLoading] = useState(false);

  // -- Tab data caches (keyed by security id) --
  const [identifiers, setIdentifiers] = useState<Identifier[]>([]);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [coupons, setCoupons] = useState<CouponSchedule[]>([]);
  const [putCallSchedule, setPutCallSchedule] = useState<PutCallSchedule[]>([]);
  const [amortization, setAmortization] = useState<AmortizationSchedule[]>([]);
  const [relationships, setRelationships] = useState<RelatedSecurity[]>([]);
  const [enhancements, setEnhancements] = useState<Enhancement[]>([]);
  const [liquidity, setLiquidity] = useState<LiquiditySnapshot | null>(null);
  const [auditTrail, setAuditTrail] = useState<AuditEntry[]>([]);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [pcDetail, setPcDetail] = useState<PcDetailPayload | null>(null);
  const [llDetail, setLlDetail] = useState<LlLoanSummary | null>(null);
  const [llRelatedSecurities, setLlRelatedSecurities] = useState<RelatedSecurity[]>([]);
  const [loanDetailLoading, setLoanDetailLoading] = useState(false);
  const [loanDetailError, setLoanDetailError] = useState<string | null>(null);
  const tabDataLoaded = useRef<Record<string, Set<TabId>>>({});

  // -- Schedules sub-tab --
  const [scheduleSubTab, setScheduleSubTab] = useState<'coupons' | 'putcall' | 'amortization'>('coupons');

  // -- Feedback --
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // -- Modal --
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<SecuritySummary>>({});
  // F-PUB-030c: sub-class picker options, scoped to the asset class typed above.
  const [assetClasses, setAssetClasses] = useState<any[]>([]);
  const [assetSubClasses, setAssetSubClasses] = useState<any[]>([]);
  // Classification FRD Phase 1 (FR-3): canonical node picker + the pick itself.
  const [classificationNodes, setClassificationNodes] = useState<any[]>([]);
  const [classificationPick, setClassificationPick] = useState<string>('');
  const [classificationLoadedFor, setClassificationLoadedFor] = useState<number | null>(null);

  // -- Stats --
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    securities.forEach(s => { counts[s.instrumentType] = (counts[s.instrumentType] || 0) + 1; });
    return counts;
  }, [securities]);

  const activeCount = useMemo(() => securities.filter(s => s.securityStatus === 'ACTIVE').length, [securities]);

  // Polish backlog item #1: chip taxonomy is derived from the fetched data,
  // not a hardcoded list.  A pure PC shop should not see a BOND chip; a
  // public-FI shop should not see a LOAN chip just because the universe of
  // instrument types includes them somewhere.  Snapshot is captured from the
  // most recent unfiltered fetch so the chip strip doesn't collapse to just
  // the active type once a filter is applied.  Preferred order first, then
  // anything else alphabetically.
  const [typeTaxonomy, setTypeTaxonomy] = useState<{ type: string; count: number }[]>([]);
  const buildTaxonomy = useCallback((rows: SecuritySummary[]) => {
    const counts = new Map<string, number>();
    rows.forEach(s => {
      const t = (s.instrumentType || '').toUpperCase();
      if (!t) return;
      counts.set(t, (counts.get(t) || 0) + 1);
    });
    const ordered: { type: string; count: number }[] = [];
    INSTRUMENT_TYPE_ORDER.forEach(t => {
      if (counts.has(t)) ordered.push({ type: t, count: counts.get(t)! });
    });
    Array.from(counts.keys())
      .filter(t => !INSTRUMENT_TYPE_ORDER.includes(t))
      .sort()
      .forEach(t => ordered.push({ type: t, count: counts.get(t)! }));
    return ordered;
  }, []);
  const dynamicInstrumentTypes = typeTaxonomy;

  // ============== Data Fetching ==============
  const fetchSecurities = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, any> = { page, size: pageSize };
      if (searchTerm) params.q = searchTerm;
      if (filterType) params.instrumentType = filterType;
      if (filterStatus) params.status = filterStatus;
      const response = await axios.get('/api/security-master/search', { params });
      const data = response.data;
      let rows: SecuritySummary[] = [];
      let total = 0;
      if (data?.content) {
        rows = data.content;
        total = data.totalElements ?? data.content.length;
      } else if (Array.isArray(data)) {
        rows = data;
        total = data.length;
      }
      setSecurities(rows);
      setTotalElements(total);
    } catch {
      // Fallback to legacy endpoint -- /api/dimensions/securities doesn't
      // support server-side filters, so apply them client-side here.  Without
      // this, a chip click looks like a no-op when the search endpoint is
      // unhealthy.
      try {
        const response = await axios.get('/api/dimensions/securities');
        const data = response.data?.content || response.data || [];
        const arr = Array.isArray(data) ? data : [];
        const mapped: SecuritySummary[] = arr.map((s: any) => ({
          id: s.id,
          assetId: s.cusip || `SEC-${s.id}`,
          securityName: s.securityName || '',
          instrumentType: s.instrumentType || s.assetClass?.assetClassName || 'BOND',
          securityStatus: s.securityStatus || 'ACTIVE',
          cusip: s.cusip,
          isin: s.isin,
          currency: s.currency || 'USD',
          couponRate: s.couponRate,
          maturityDate: s.maturityDate,
          faceValue: s.faceValue,
          issuerName: s.issuer?.issuerName,
          issuerId: s.issuer?.id,
          assetClass: s.assetClass?.assetClassName,
          updatedAt: s.updatedAt,
        }));
        // Refresh chip taxonomy from the full unfiltered set BEFORE we apply
        // client-side filters.
        setTypeTaxonomy(buildTaxonomy(mapped));
        let filtered = mapped;
        if (filterType) {
          filtered = filtered.filter(s => (s.instrumentType || '').toUpperCase() === filterType.toUpperCase());
        }
        if (filterStatus) {
          filtered = filtered.filter(s => (s.securityStatus || '').toUpperCase() === filterStatus.toUpperCase());
        }
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          filtered = filtered.filter(s =>
            (s.securityName || '').toLowerCase().includes(q) ||
            (s.cusip || '').toLowerCase().includes(q) ||
            (s.isin || '').toLowerCase().includes(q) ||
            (s.assetId || '').toLowerCase().includes(q)
          );
        }
        setSecurities(filtered);
        setTotalElements(filtered.length);
      } catch {
        setSecurities([]);
        setTotalElements(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchTerm, filterType, filterStatus, buildTaxonomy]);

  useEffect(() => { fetchSecurities(); }, [fetchSecurities]);

  // F-PUB-030c: load the asset class / sub-class dimensions once so the New
  // Security form can offer a real sub-class instead of leaving the FK NULL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [acRes, ascRes, nodeRes] = await Promise.all([
          axios.get('/api/dimensions/asset-classes'),
          axios.get('/api/dimensions/asset-sub-classes'),
          axios.get('/api/classification/nodes'),
        ]);
        if (cancelled) return;
        const asArray = (d: any) => (Array.isArray(d) ? d : d?.content || []);
        setAssetClasses(asArray(acRes.data));
        setAssetSubClasses(asArray(ascRes.data));
        setClassificationNodes(Array.isArray(nodeRes.data) ? nodeRes.data : []);
      } catch {
        // Non-fatal: the field falls back to backend derivation.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Options for the sub-class picker, narrowed to the asset class the user typed
  // (by code or name). With no match we show everything rather than an empty list.
  const assetSubClassOptions = useMemo(() => {
    const typed = (formData.assetClass || '').trim().toLowerCase();
    if (!typed) return assetSubClasses;
    const match = assetClasses.find((ac: any) =>
      String(ac.assetClassCode || '').toLowerCase() === typed
      || String(ac.assetClassName || '').toLowerCase() === typed);
    if (!match) return assetSubClasses;
    const scoped = assetSubClasses.filter((asc: any) =>
      (asc.assetClassId ?? asc.assetClass?.id) === match.id);
    return scoped.length > 0 ? scoped : assetSubClasses;
  }, [assetClasses, assetSubClasses, formData.assetClass]);

  // Chip taxonomy: fetch the full universe (single large page) once so the
  // chip counts reflect the entire tenant book, not just the current page.
  // Re-runs only when the tenant context changes (mount).  Using size=2000
  // is enough headroom for any single tenant -- if a tenant exceeds that
  // we add a /facets endpoint, but no current tenant is close.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get('/api/security-master/search', { params: { page: 0, size: 2000 } });
        const data = response.data;
        const rows: SecuritySummary[] = data?.content || (Array.isArray(data) ? data : []);
        if (!cancelled) setTypeTaxonomy(buildTaxonomy(rows));
      } catch {
        try {
          const response = await axios.get('/api/dimensions/securities');
          const data = response.data?.content || response.data || [];
          const arr = Array.isArray(data) ? data : [];
          const mapped = arr.map((s: any) => ({
            instrumentType: s.instrumentType || s.assetClass?.assetClassName || 'BOND',
          })) as SecuritySummary[];
          if (!cancelled) setTypeTaxonomy(buildTaxonomy(mapped));
        } catch {
          // leave taxonomy empty -- chips just won't render
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: ?cusip=XXXXXXXXX (or isin/ticker) auto-opens that security's
  // detail panel. Used by IBOR Command and Trade Blotter CUSIP click-throughs.
  // The identifier remains in the URL so the selected SMF record is bookmarkable.
  useEffect(() => {
    const deepLinkCusip = searchParams.get('cusip') || searchParams.get('isin') || searchParams.get('ticker');
    if (deepLinkCusip) {
      resolveIdentifier(deepLinkCusip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Try universal resolution first if it looks like an identifier
    const trimmed = searchInput.trim();
    if (trimmed && /^[A-Z0-9]{5,12}$/i.test(trimmed)) {
      resolveIdentifier(trimmed);
    } else {
      setSearchTerm(trimmed);
      setPage(0);
    }
  };

  const resolveIdentifier = async (identifier: string) => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/security-master/resolve', { params: { identifier } });
      if (response.data) {
        setSelectedSecurity(response.data);
        setActiveTab('overview');
        tabDataLoaded.current = {};
        setPcDetail(null);
        setLlDetail(null);
        setLoanDetailError(null);
        if (response.data.instrumentType === 'LOAN') {
          fetchPcDetail(response.data.id);
        } else {
          setLoanDetailLoading(false);
        }
      }
    } catch {
      // Fall back to search
      setSearchTerm(identifier);
      setPage(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSecurityDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`/api/security-master/${id}`);
      setSelectedSecurity(response.data);
    } catch { /* keep existing selection */ }
    finally { setDetailLoading(false); }
  }, []);

  // LOAN-conditional: resolve both governed credit masters. A security belongs
  // to one of them; the successful response supplies the cross-workspace link.
  const fetchPcDetail = useCallback(async (secId: number) => {
    setLoanDetailLoading(true);
    setLoanDetailError(null);
    try {
      const [pc, ll, llReference] = await Promise.allSettled([
        axios.get(`/api/security-master/${secId}/private-credit-detail`),
        axios.get(`/api/credit/loans/by-security/${secId}`),
        axios.get(`/api/security-master/${secId}/leveraged-loan-detail`),
      ]);
      const pcData = pc.status === 'fulfilled' ? pc.value.data as PcDetailPayload : null;
      const llData = ll.status === 'fulfilled' && ll.value.data?.id
        ? ll.value.data as LlLoanSummary
        : null;
      const llReferenceData = llReference.status === 'fulfilled'
        ? llReference.value.data as PcDetailPayload
        : null;
      setPcDetail(pcData?.loan ? pcData : (llReferenceData?.loan ? llReferenceData : null));
      setLlDetail(llData);
      setLlRelatedSecurities(llReferenceData?.relatedSecurities || []);
      if (!pcData?.loan && !llData) {
        setLoanDetailError('No governed loan record is linked to this SMF security.');
      }
    } finally {
      setLoanDetailLoading(false);
    }
  }, []);

  const fetchTabData = useCallback(async (secId: number, tab: TabId) => {
    const key = String(secId);
    if (!tabDataLoaded.current[key]) tabDataLoaded.current[key] = new Set();
    if (tabDataLoaded.current[key].has(tab)) return;
    tabDataLoaded.current[key].add(tab);

    try {
      switch (tab) {
        case 'identifiers': {
          const r = await axios.get(`/api/security-master/${secId}/identifiers`);
          setIdentifiers(Array.isArray(r.data) ? r.data : []);
          break;
        }
        case 'ratings': {
          const r = await axios.get(`/api/security-master/${secId}/ratings`);
          setRatings(Array.isArray(r.data) ? r.data : []);
          break;
        }
        case 'schedules': {
          const [c, pc, am] = await Promise.all([
            axios.get(`/api/security-master/${secId}/coupons`).catch(() => ({ data: [] })),
            axios.get(`/api/security-master/${secId}/put-call-schedule`).catch(() => ({ data: [] })),
            axios.get(`/api/security-master/${secId}/amortization-schedule`).catch(() => ({ data: [] })),
          ]);
          setCoupons(Array.isArray(c.data) ? c.data : []);
          setPutCallSchedule(Array.isArray(pc.data) ? pc.data : []);
          setAmortization(Array.isArray(am.data) ? am.data : []);
          break;
        }
        case 'relationships': {
          const r = await axios.get(`/api/security-master/${secId}/related`);
          setRelationships(Array.isArray(r.data) ? r.data : []);
          break;
        }
        case 'enhancements': {
          const [e, cl] = await Promise.all([
            axios.get(`/api/security-master/${secId}/enhancements`).catch(() => ({ data: [] })),
            axios.get(`/api/security-master/${secId}/classifications`).catch(() => ({ data: [] })),
          ]);
          setEnhancements(Array.isArray(e.data) ? e.data : []);
          setClassifications(Array.isArray(cl.data) ? cl.data : []);
          break;
        }
        case 'liquidity': {
          const r = await axios.get(`/api/security-master/${secId}/liquidity`);
          setLiquidity(r.data || null);
          break;
        }
        case 'audit': {
          const r = await axios.get(`/api/security-master/${secId}/audit-trail`);
          setAuditTrail(Array.isArray(r.data) ? r.data : []);
          break;
        }
      }
    } catch {
      // silently handle - data simply won't be shown
    }
  }, []);

  // Lazy fetch tab data when tab changes
  useEffect(() => {
    if (selectedSecurity && activeTab !== 'overview') {
      fetchTabData(selectedSecurity.id, activeTab);
    }
  }, [selectedSecurity, activeTab, fetchTabData]);

  // ============== Row Selection ==============
  const handleRowClick = (sec: SecuritySummary) => {
    if (selectedSecurity?.id === sec.id) return;
    tabDataLoaded.current = {};
    setSelectedSecurity(sec);
    setActiveTab('overview');
    setPcDetail(null);
    setLlDetail(null);
    setLoanDetailError(null);
    const next = new URLSearchParams(searchParams);
    next.delete('isin');
    next.delete('ticker');
    if (sec.cusip || sec.assetId) next.set('cusip', sec.cusip || sec.assetId);
    setSearchParams(next, { replace: true });
    // Fetch full detail in background
    fetchSecurityDetail(sec.id);
    if (sec.instrumentType === 'LOAN') {
      fetchPcDetail(sec.id);
    } else {
      setLoanDetailLoading(false);
    }
  };

  // ============== Sorting ==============
  const sortedSecurities = useMemo(() => {
    const arr = [...securities];
    arr.sort((a, b) => {
      const av = (a as any)[sortCol] ?? '';
      const bv = (b as any)[sortCol] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [securities, sortCol, sortDir]);

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const isSelectedLoanSecurity = (selectedSecurity?.instrumentType || '').toUpperCase() === 'LOAN';
  const linkedLoanTarget = llDetail
    ? {
        source: 'LEVERAGED_LOAN' as const,
        id: llDetail.id,
        label: llDetail.loanName || llDetail.borrowerName || llDetail.loanId || selectedSecurity?.securityName || 'Leveraged loan',
        meta: [llDetail.facilityType, llDetail.status].filter(Boolean).join(' · '),
        facilityType: llDetail.facilityType,
      }
    : pcDetail?.loan
      ? {
          source: 'PRIVATE_CREDIT' as const,
          id: pcDetail.loan.id,
          label: pcDetail.loan.loanName || pcDetail.loan.borrowerName || pcDetail.loan.loanId || selectedSecurity?.securityName || 'Private credit loan',
          meta: [pcDetail.loan.facilityType, pcDetail.loan.status].filter(Boolean).join(' · '),
          facilityType: pcDetail.loan.facilityType,
        }
      : null;

  // ============== Add / Edit ==============
  const handleAdd = () => {
    setFormData({ currency: 'USD', instrumentType: 'BOND', securityStatus: 'ACTIVE', masterStatus: 'PENDING', quantityType: 'FACE' });
    setClassificationPick('');
    setClassificationLoadedFor(null);
    setIsEditing(false);
    setShowModal(true);
    setError(null);
  };

  const handleEdit = () => {
    if (!selectedSecurity) return;
    setFormData({ ...selectedSecurity });
    // Hydrate the current canonical assignment for the picker (FR-3).
    setClassificationPick('');
    setClassificationLoadedFor(selectedSecurity.id);
    axios.get(`/api/classification/securities/${selectedSecurity.id}`)
      .then(r => setClassificationPick(r.data?.nodeCode || ''))
      .catch(() => setClassificationPick(''));
    setIsEditing(true);
    setShowModal(true);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // Backend DTO expects couponFrequency as an Integer (coupons per year);
      // the select emits a string label, so translate it before submit.
      const COUPON_FREQ_TO_INT: Record<string, number> = {
        MONTHLY: 12, QUARTERLY: 4, SEMI_ANNUAL: 2, ANNUAL: 1, ZERO: 0,
      };
      const rawFreq = (formData as any).couponFrequency;
      const payload: any = { ...formData };
      if (typeof rawFreq === 'string') {
        payload.couponFrequency = rawFreq === '' ? null : (COUPON_FREQ_TO_INT[rawFreq] ?? null);
      }
      let savedId: number | undefined;
      if (isEditing && formData.id) {
        await axios.put(`/api/security-master/${formData.id}`, payload);
        savedId = formData.id;
        setSuccessMessage('Security updated successfully');
        fetchSecurityDetail(formData.id);
      } else {
        const resp = await axios.post('/api/security-master', payload);
        savedId = resp.data?.id;
        setSuccessMessage('Security created successfully');
        if (resp.data?.id) fetchSecurityDetail(resp.data.id);
      }
      // Classification FRD Phase 1: persist an explicit canonical pick
      // (ASSERTED). Shadow path — a failure logs but never fails the save.
      if (savedId && classificationPick) {
        try {
          await axios.put(`/api/classification/securities/${savedId}`, { nodeCode: classificationPick });
        } catch (clsErr) {
          console.warn('Canonical classification save failed (shadow path):', clsErr);
        }
      }
      setShowModal(false);
      fetchSecurities();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save security');
    } finally {
      setIsSaving(false);
    }
  };

  const updateForm = (patch: Partial<SecuritySummary>) => setFormData(prev => ({ ...prev, ...patch }));

  // ============== Table Column Defs ==============
  const columns: { key: string; label: string; width?: string; mono?: boolean }[] = [
    { key: 'assetId', label: 'Asset ID', width: '100px', mono: true },
    { key: 'securityName', label: 'Name' },
    { key: 'instrumentType', label: 'Type', width: '90px' },
    { key: 'securityStatus', label: 'Status', width: '85px' },
    { key: 'maturityDate', label: 'Maturity', width: '100px' },
    { key: 'couponRate', label: 'Coupon', width: '75px' },
    { key: 'currency', label: 'CCY', width: '55px' },
    { key: 'issuerName', label: 'Issuer', width: '120px' },
  ];

  // ============== Render ==============
  return (
    <Container>
      <div className="max-w-[1920px] mx-auto space-y-5">
        {/* Header */}
        <motion.div initial="initial" animate="animate" variants={{ animate: { transition: { staggerChildren: 0.06 } } }} className="space-y-5">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                <Database className="w-7 h-7 text-blue-500" />
                Security Master
              </h1>
              <p className="text-slate-500 font-medium text-sm mt-0.5">Unified multi-asset security reference hub</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => fetchSecurities()}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-xs font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
                Refresh
              </button>
              {canManageSecurityMaster && (
                <button
                  onClick={handleAdd}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-xl text-xs font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition-all"
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  Add Security
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard label="Total Securities" value={totalElements} icon={<Database className="w-4 h-4" />} color="#3b82f6" />
            <StatCard label="Active" value={activeCount} icon={<Activity className="w-4 h-4" />} color="#10b981" />
            {Object.entries(typeCounts).slice(0, 3).map(([type, count]) => (
              <StatCard key={type} label={type} value={count} icon={<Layers className="w-4 h-4" />} color={INSTRUMENT_COLORS[type] || '#64748b'} />
            ))}
            <StatCard label="Data Health" value="99.8%" icon={<Shield className="w-4 h-4" />} color="#f59e0b" />
          </div>
        </motion.div>

        {/* Success / Error Toasts */}
        <AnimatePresence>
          {successMessage && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-400 font-bold text-sm">
              <CheckCircle2 className="w-4 h-4" /> {successMessage}
            </motion.div>
          )}
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400 font-bold text-sm">
              <AlertCircle className="w-4 h-4" /> {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-300"><X className="w-4 h-4" /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Master-Detail Layout */}
        <div className="flex gap-4" style={{ height: 'calc(100vh - 310px)', minHeight: '500px' }}>
          {/* Left Panel - Securities Grid */}
          <GlassCard className="flex flex-col" style={{ flex: selectedSecurity ? '0 0 60%' : '1 1 100%', transition: 'flex 0.3s ease' }}>
            {/* Search & Filters */}
            <div className="p-3 border-b border-white/5 space-y-2.5 bg-slate-900/30">
              <form onSubmit={handleSearchSubmit} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search by CUSIP, ISIN, SEDOL, ticker, or name..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-slate-950/50 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 transition-all"
                  />
                </div>
                <button type="submit" className="px-4 py-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-xs font-bold hover:bg-blue-600/30 transition-all">
                  Resolve
                </button>
              </form>
              <div className="flex gap-1.5 flex-wrap items-center">
                <Filter className="w-3 h-3 text-slate-600 mr-1" />
                <FilterChip label="All Types" active={!filterType} onClick={() => { setFilterType(null); setPage(0); }} />
                {dynamicInstrumentTypes.map(({ type, count }) => (
                  <FilterChip key={type} label={`${type} (${count})`} active={filterType === type} color={INSTRUMENT_COLORS[type]} onClick={() => { setFilterType(filterType === type ? null : type); setPage(0); }} />
                ))}
                <span className="w-px h-4 bg-white/10 mx-1" />
                <FilterChip label="All Status" active={!filterStatus} onClick={() => { setFilterStatus(null); setPage(0); }} />
                {(['ACTIVE', 'MATURED', 'DEFAULTED', 'CALLED'] as const).map(s => (
                  <FilterChip key={s} label={s} active={filterStatus === s} color={STATUS_COLORS[s]} onClick={() => { setFilterStatus(filterStatus === s ? null : s); setPage(0); }} />
                ))}
              </div>
            </div>

            {/* Table */}
            <CustomScrollbar className="flex-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-950/80 backdrop-blur-sm">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer hover:text-slate-300 transition-colors select-none"
                        style={{ width: col.width }}
                        onClick={() => toggleSort(col.key)}
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          {sortCol === col.key ? (
                            <ArrowUpDown className="w-3 h-3 text-blue-400" />
                          ) : (
                            <ChevronsUpDown className="w-3 h-3 text-slate-700" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && securities.length === 0 ? (
                    <tr><td colSpan={columns.length} className="px-4 py-16 text-center">
                      <RefreshCcw className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
                      <span className="text-slate-500 text-xs">Loading securities...</span>
                    </td></tr>
                  ) : sortedSecurities.length === 0 ? (
                    <tr><td colSpan={columns.length} className="px-4 py-16 text-center text-slate-600 text-xs">No securities found</td></tr>
                  ) : sortedSecurities.map((sec) => (
                    <tr
                      key={sec.id}
                      onClick={() => handleRowClick(sec)}
                      className={cn(
                        'border-b border-white/[0.03] cursor-pointer transition-colors',
                        selectedSecurity?.id === sec.id ? 'bg-blue-500/10' : 'hover:bg-white/[0.02]'
                      )}
                    >
                      <td className="px-3 py-2.5 font-mono text-blue-400 font-semibold text-xs">{sec.assetId || sec.cusip || `#${sec.id}`}</td>
                      <td className="px-3 py-2.5 text-white font-medium truncate max-w-[200px]">{sec.securityName}</td>
                      <td className="px-3 py-2.5"><Badge label={sec.instrumentType || 'BOND'} colorMap={INSTRUMENT_COLORS} /></td>
                      <td className="px-3 py-2.5"><Badge label={sec.securityStatus || 'ACTIVE'} colorMap={STATUS_COLORS} /></td>
                      <td className="px-3 py-2.5 text-slate-400 font-mono text-xs">{sec.maturityDate || '--'}</td>
                      <td className="px-3 py-2.5 text-slate-300 font-mono text-xs">{sec.couponRate != null ? `${sec.couponRate}%` : '--'}</td>
                      <td className="px-3 py-2.5 text-slate-400 text-xs text-center">{sec.currency}</td>
                      <td className="px-3 py-2.5 text-xs truncate max-w-[120px]">
                        {sec.issuerId && sec.issuerName ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/issuers/${sec.issuerId}`); }}
                            className="text-blue-300 hover:text-blue-200 hover:underline text-left truncate"
                            title={`View ${sec.issuerName} profile`}
                          >
                            {sec.issuerName}
                          </button>
                        ) : (
                          <span className="text-slate-400">{sec.issuerName || '--'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CustomScrollbar>

            {/* Pagination */}
            <div className="px-4 py-2.5 border-t border-white/5 bg-slate-900/30 flex items-center justify-between text-xs">
              <span className="text-slate-500">
                Showing {securities.length} of {totalElements} securities
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  className="px-3 py-1 rounded-lg border border-white/10 text-slate-400 hover:text-white disabled:opacity-30 transition-all"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-slate-400">Page {page + 1}</span>
                <button
                  disabled={securities.length < pageSize}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1 rounded-lg border border-white/10 text-slate-400 hover:text-white disabled:opacity-30 transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          </GlassCard>

          {/* Right Panel - Detail */}
          <AnimatePresence mode="wait">
            {selectedSecurity ? (
              <motion.div
                key="detail"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ duration: 0.25 }}
                style={{ flex: '0 0 40%' }}
              >
                <GlassCard className="flex flex-col h-full">
                  {/* Detail Header */}
                  <div className="p-4 border-b border-white/5 bg-slate-900/30">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge label={selectedSecurity.instrumentType || 'BOND'} colorMap={INSTRUMENT_COLORS} />
                          <Badge label={selectedSecurity.securityStatus || 'ACTIVE'} colorMap={STATUS_COLORS} />
                        </div>
                        <h2 className="text-lg font-bold text-white truncate">{selectedSecurity.securityName}</h2>
                        <div className="flex gap-3 mt-1 text-xs text-slate-500 font-mono">
                          {selectedSecurity.cusip && <span>CUSIP: <span className="text-slate-300">{selectedSecurity.cusip}</span></span>}
                          {selectedSecurity.isin && <span>ISIN: <span className="text-slate-300">{selectedSecurity.isin}</span></span>}
                        </div>
                      </div>
                      <div className="flex gap-1.5 ml-2 flex-shrink-0">
                        {canManageSecurityMaster && (
                          <button onClick={handleEdit} className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all border border-blue-500/20" title="Edit Security">
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => {
                          setSelectedSecurity(null);
                          setLlDetail(null);
                          const next = new URLSearchParams(searchParams);
                          next.delete('cusip'); next.delete('isin'); next.delete('ticker');
                          setSearchParams(next, { replace: true });
                        }} className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5" title="Close">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>

                    {isSelectedLoanSecurity && (
                      <div className="px-4 py-3 border-b border-white/5 bg-cyan-950/15">
                        {loanDetailLoading ? (
                          <div className="flex items-center gap-2 text-xs font-semibold text-cyan-200">
                            <RefreshCcw className="w-3.5 h-3.5 animate-spin" />
                            Resolving governed loan record...
                          </div>
                        ) : linkedLoanTarget ? (
                          <div className="flex flex-col gap-3">
                            <div className="min-w-0">
                              <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">
                                Linked loan record
                              </div>
                              <div className="truncate text-sm font-semibold text-white">
                                {linkedLoanTarget.label}
                              </div>
                              <div className="text-xs text-slate-400">
                                {linkedLoanTarget.meta || (linkedLoanTarget.source === 'LEVERAGED_LOAN' ? 'Leveraged loan' : 'Private credit')}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => navigate(loan360Path(linkedLoanTarget.source, linkedLoanTarget.id))}
                                className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-2 text-xs font-bold text-cyan-200 transition-all hover:bg-cyan-500/20"
                              >
                                <BookOpen className="h-3.5 w-3.5" />
                                <span className="truncate">Open Loan 360</span>
                              </button>
                              {linkedLoanTarget.source === 'LEVERAGED_LOAN' && selectedSecurity.cusip && (
                                <button
                                  type="button"
                                  onClick={() => navigate(
                                    leveragedLoanTicketPath(selectedSecurity.cusip),
                                    leveragedLoanTicketState({
                                      securityId: selectedSecurity.id,
                                      cusip: selectedSecurity.cusip,
                                      securityName: selectedSecurity.securityName,
                                      loanId: linkedLoanTarget.id,
                                      loanName: linkedLoanTarget.label,
                                      facilityType: linkedLoanTarget.facilityType,
                                    }),
                                  )}
                                  className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-lg border border-violet-400/30 bg-violet-500/10 px-2.5 py-2 text-xs font-bold text-violet-200 transition-all hover:bg-violet-500/20"
                                >
                                  <Ticket className="h-3.5 w-3.5" />
                                  <span className="truncate">Trade Ticket</span>
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2 text-xs font-semibold text-amber-200">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <span>{loanDetailError || 'Loan security selected, but no governed loan record is linked yet.'}</span>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Tabs */}
                  <div className="flex border-b border-white/5 overflow-x-auto bg-slate-950/30">
                    {DETAIL_TABS.map(tab => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={cn(
                            'flex items-center gap-1.5 px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap transition-all border-b-2',
                            activeTab === tab.id
                              ? 'text-blue-400 border-blue-500'
                              : 'text-slate-500 border-transparent hover:text-slate-300'
                          )}
                        >
                          <Icon className="w-3 h-3" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab Content */}
                  <CustomScrollbar className="flex-1 p-4">
                    {detailLoading && activeTab === 'overview' ? (
                      <div className="flex items-center justify-center py-12">
                        <RefreshCcw className="w-5 h-5 text-blue-500 animate-spin" />
                      </div>
                    ) : (
                      <>
                        {activeTab === 'overview' && <OverviewTab security={selectedSecurity} pcDetail={pcDetail} />}
                        {activeTab === 'identifiers' && <IdentifiersTab identifiers={identifiers} securityId={selectedSecurity.id} onRefresh={() => { tabDataLoaded.current = {}; fetchTabData(selectedSecurity.id, 'identifiers'); }} />}
                        {activeTab === 'ratings' && <RatingsTab ratings={ratings} securityId={selectedSecurity.id} onRefresh={() => { tabDataLoaded.current = {}; fetchTabData(selectedSecurity.id, 'ratings'); }} />}
                        {activeTab === 'schedules' && (
                          <SchedulesTab
                            coupons={coupons}
                            putCall={putCallSchedule}
                            amortization={amortization}
                            subTab={scheduleSubTab}
                            setSubTab={setScheduleSubTab}
                          />
                        )}
                        {activeTab === 'relationships' && (
                          <RelationshipsTab
                            relationships={[...relationships, ...llRelatedSecurities]}
                            lenders={pcDetail?.lenderRegister || []}
                          />
                        )}
                        {activeTab === 'enhancements' && <EnhancementsTab enhancements={enhancements} classifications={classifications} securityId={selectedSecurity.id} onRefresh={() => { tabDataLoaded.current = {}; fetchTabData(selectedSecurity.id, 'enhancements'); }} />}
                        {activeTab === 'liquidity' && <LiquidityTab data={liquidity} />}
                        {activeTab === 'audit' && <AuditTrailTab entries={auditTrail} />}
                      </>
                    )}
                  </CustomScrollbar>
                </GlassCard>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="hidden"
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
              onClick={() => setShowModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-3xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-white/5 bg-slate-950/30 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-white">{isEditing ? 'Edit Security' : 'Add New Security'}</h2>
                  <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-0.5">
                    Security Master File · Owner: Reference Data / Middle Office · Role: {userRole || 'UNKNOWN'}
                  </p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 text-slate-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
              </div>

              <CustomScrollbar className="p-6 space-y-5 max-h-[70vh]">
                {/* Core */}
                <SectionLabel>Core Identification</SectionLabel>
                <div className="grid grid-cols-3 gap-4">
                  <FormField label="Asset ID">
                    <input value={formData.assetId || ''} onChange={e => updateForm({ assetId: e.target.value })} className={cn(inputCls, 'font-mono')} placeholder="Auto-generated if blank" />
                  </FormField>
                  <FormField label="Security Name" span={2}>
                    <input value={formData.securityName || ''} onChange={e => updateForm({ securityName: e.target.value })} className={inputCls} placeholder="Full legal name" />
                  </FormField>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <FormField label="Instrument Type">
                    <select value={formData.instrumentType || 'BOND'} onChange={e => updateForm({ instrumentType: e.target.value })} className={selectCls}>
                      {INSTRUMENT_TYPE_ORDER.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Instrument Subtype">
                    <input value={formData.instrumentSubtype || ''} onChange={e => updateForm({ instrumentSubtype: e.target.value })} className={inputCls} placeholder="e.g. CORPORATE, SOVEREIGN" />
                  </FormField>
                  <FormField label="Quantity Type">
                    <select value={formData.quantityType || 'FACE'} onChange={e => updateForm({ quantityType: e.target.value })} className={selectCls}>
                      <option value="FACE">FACE</option>
                      <option value="SHARES">SHARES</option>
                      <option value="CONTRACTS">CONTRACTS</option>
                      <option value="NOTIONAL">NOTIONAL</option>
                      <option value="UNITS">UNITS</option>
                    </select>
                  </FormField>
                </div>

                {/* Identifiers */}
                <SectionLabel>Identifiers</SectionLabel>
                <div className="grid grid-cols-3 gap-4">
                  <FormField label="CUSIP">
                    <input value={formData.cusip || ''} onChange={e => updateForm({ cusip: e.target.value })} className={cn(inputCls, 'font-mono')} placeholder="9-digit" />
                  </FormField>
                  <FormField label="ISIN">
                    <input value={formData.isin || ''} onChange={e => updateForm({ isin: e.target.value })} className={cn(inputCls, 'font-mono')} placeholder="12-character" />
                  </FormField>
                  <FormField label="SEDOL">
                    <input value={formData.sedol || ''} onChange={e => updateForm({ sedol: e.target.value })} className={cn(inputCls, 'font-mono')} placeholder="7-character" />
                  </FormField>
                </div>

                {/* Fixed Income */}
                {(formData.instrumentType === 'BOND' || formData.instrumentType === 'LOAN') && (
                  <>
                    <SectionLabel>Fixed Income Terms</SectionLabel>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Coupon Rate (%)">
                        <input type="number" step="0.001" value={formData.couponRate ?? ''} onChange={e => updateForm({ couponRate: e.target.value ? parseFloat(e.target.value) : undefined })} className={cn(inputCls, 'font-mono')} placeholder="0.000" />
                      </FormField>
                      <FormField label="Face Value">
                        <input type="number" value={formData.faceValue ?? ''} onChange={e => updateForm({ faceValue: e.target.value ? parseFloat(e.target.value) : undefined })} className={cn(inputCls, 'font-mono')} placeholder="1000" />
                      </FormField>
                      <FormField label="Maturity Date">
                        <DateField value={formData.maturityDate || ''} onChange={iso => updateForm({ maturityDate: iso })} />
                      </FormField>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Day Count Convention">
                        <select value={formData.dayCountConvention || ''} onChange={e => updateForm({ dayCountConvention: e.target.value })} className={selectCls}>
                          <option value="">Select</option>
                          <option value="30/360">30/360</option>
                          <option value="ACT/360">ACT/360</option>
                          <option value="ACT/365">ACT/365</option>
                          <option value="ACT/ACT">ACT/ACT</option>
                        </select>
                      </FormField>
                      <FormField label="Coupon Frequency">
                        <select value={formData.couponFrequency || ''} onChange={e => updateForm({ couponFrequency: e.target.value })} className={selectCls}>
                          <option value="">Select</option>
                          <option value="MONTHLY">Monthly</option>
                          <option value="QUARTERLY">Quarterly</option>
                          <option value="SEMI_ANNUAL">Semi-Annual</option>
                          <option value="ANNUAL">Annual</option>
                          <option value="ZERO">Zero Coupon</option>
                        </select>
                      </FormField>
                    </div>
                  </>
                )}

                {/* Classification */}
                <SectionLabel>Classification</SectionLabel>
                <div className="grid grid-cols-3 gap-4">
                  <FormField label="Currency">
                    <select value={formData.currency || 'USD'} onChange={e => updateForm({ currency: e.target.value })} className={selectCls}>
                      {['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY', 'HKD', 'SGD'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Issuer Name">
                    <input value={formData.issuerName || ''} onChange={e => updateForm({ issuerName: e.target.value })} className={inputCls} placeholder="Issuer entity" />
                  </FormField>
                  <FormField label="Asset Class">
                    <input value={formData.assetClass || ''} onChange={e => updateForm({ assetClass: e.target.value })} className={inputCls} placeholder="e.g. Fixed Income" />
                  </FormField>
                  {/* F-PUB-030c: the security master is the source of truth for
                      sub-class. Left blank the backend derives it from the
                      instrument text and credit rating; picking here overrides. */}
                  <FormField label="Asset Sub Class">
                    <select value={formData.assetSubClass || ''} onChange={e => updateForm({ assetSubClass: e.target.value })} className={selectCls}>
                      <option value="">Derive from rating &amp; instrument</option>
                      {assetSubClassOptions.map(asc => (
                        <option key={asc.id} value={asc.assetSubClassName}>{asc.assetSubClassName}</option>
                      ))}
                    </select>
                  </FormField>
                  {/* Classification FRD Phase 1 (FR-3): canonical L1→L2(→L3) pick.
                      Leaf nodes only — an L1 alone is a partial path (§6.2). */}
                  <FormField label="Canonical Classification">
                    <select value={classificationPick} onChange={e => setClassificationPick(e.target.value)} className={selectCls}>
                      <option value="">Derive from legacy mapping</option>
                      {classificationNodes.flatMap((l1: any) =>
                        (l1.children || []).flatMap((l2: any) => [
                          <option key={l2.code} value={l2.code}>{l1.name} → {l2.name}</option>,
                          ...(l2.children || []).map((l3: any) => (
                            <option key={l3.code} value={l3.code}>{l1.name} → {l2.name} → {l3.name}</option>
                          )),
                        ]))}
                    </select>
                  </FormField>
                </div>

                {/* Lifecycle */}
                <SectionLabel>Lifecycle & Governance</SectionLabel>
                <div className="grid grid-cols-3 gap-4">
                  <FormField label="Security Status">
                    <select value={formData.securityStatus || 'ACTIVE'} onChange={e => updateForm({ securityStatus: e.target.value })} className={selectCls}>
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Master Status">
                    <select value={formData.masterStatus || 'PENDING'} onChange={e => updateForm({ masterStatus: e.target.value })} className={selectCls}>
                      <option value="PENDING">PENDING</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="GOLDEN">GOLDEN</option>
                      <option value="STALE">STALE</option>
                    </select>
                  </FormField>
                  <FormField label="Golden Source">
                    <input value={formData.goldenSource || ''} onChange={e => updateForm({ goldenSource: e.target.value })} className={inputCls} placeholder="e.g. Bloomberg, ICE" />
                  </FormField>
                </div>

                {/* Derivative fields */}
                {formData.instrumentType === 'DERIVATIVE' && (
                  <>
                    <SectionLabel>Derivative Terms</SectionLabel>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Derivative Type">
                        <select value={formData.derivativeType || ''} onChange={e => updateForm({ derivativeType: e.target.value })} className={selectCls}>
                          <option value="">Select</option>
                          <option value="CDS">CDS</option>
                          <option value="IRS">IRS</option>
                          <option value="OPTION">Option</option>
                          <option value="FUTURE">Future</option>
                          <option value="SWAP">Swap</option>
                          <option value="SWAPTION">Swaption</option>
                        </select>
                      </FormField>
                      <FormField label="Expiration Date">
                        <DateField value={formData.expirationDate || ''} onChange={iso => updateForm({ expirationDate: iso })} />
                      </FormField>
                      <FormField label="Strike Price">
                        <input type="number" step="0.01" value={formData.strikePrice ?? ''} onChange={e => updateForm({ strikePrice: e.target.value ? parseFloat(e.target.value) : undefined })} className={cn(inputCls, 'font-mono')} />
                      </FormField>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Option Type">
                        <select value={formData.optionType || ''} onChange={e => updateForm({ optionType: e.target.value })} className={selectCls}>
                          <option value="">N/A</option>
                          <option value="CALL">Call</option>
                          <option value="PUT">Put</option>
                        </select>
                      </FormField>
                      <FormField label="Exercise Style">
                        <select value={formData.exerciseStyle || ''} onChange={e => updateForm({ exerciseStyle: e.target.value })} className={selectCls}>
                          <option value="">N/A</option>
                          <option value="AMERICAN">American</option>
                          <option value="EUROPEAN">European</option>
                          <option value="BERMUDAN">Bermudan</option>
                        </select>
                      </FormField>
                      <FormField label="Notional Amount">
                        <input type="number" value={formData.notionalAmount ?? ''} onChange={e => updateForm({ notionalAmount: e.target.value ? parseFloat(e.target.value) : undefined })} className={cn(inputCls, 'font-mono')} />
                      </FormField>
                    </div>
                  </>
                )}

                {/* FX fields */}
                {formData.instrumentType === 'FX' && (
                  <>
                    <SectionLabel>FX Terms</SectionLabel>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Base Currency">
                        <input value={formData.baseCurrency || ''} onChange={e => updateForm({ baseCurrency: e.target.value })} className={cn(inputCls, 'font-mono')} placeholder="USD" />
                      </FormField>
                      <FormField label="Quote Currency">
                        <input value={formData.quoteCurrency || ''} onChange={e => updateForm({ quoteCurrency: e.target.value })} className={cn(inputCls, 'font-mono')} placeholder="EUR" />
                      </FormField>
                      <FormField label="Settlement Currency">
                        <input value={formData.settlementCurrency || ''} onChange={e => updateForm({ settlementCurrency: e.target.value })} className={cn(inputCls, 'font-mono')} placeholder="USD" />
                      </FormField>
                    </div>
                  </>
                )}

                {/* Loan fields */}
                {formData.instrumentType === 'LOAN' && (
                  <>
                    <SectionLabel>Loan Terms</SectionLabel>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Facility Type">
                        <select value={formData.facilityType || ''} onChange={e => updateForm({ facilityType: e.target.value })} className={selectCls}>
                          <option value="">Select</option>
                          <option value="TERM_LOAN_A">Term Loan A</option>
                          <option value="TERM_LOAN_B">Term Loan B</option>
                          <option value="REVOLVER">Revolver</option>
                          <option value="DELAYED_DRAW">Delayed Draw</option>
                        </select>
                      </FormField>
                      <FormField label="Seniority">
                        <select value={formData.seniority || ''} onChange={e => updateForm({ seniority: e.target.value })} className={selectCls}>
                          <option value="">Select</option>
                          <option value="SENIOR_SECURED">Senior Secured</option>
                          <option value="SENIOR_UNSECURED">Senior Unsecured</option>
                          <option value="SUBORDINATED">Subordinated</option>
                          <option value="MEZZANINE">Mezzanine</option>
                        </select>
                      </FormField>
                      <FormField label="Base Rate Index">
                        <select value={formData.baseRateIndex || ''} onChange={e => updateForm({ baseRateIndex: e.target.value })} className={selectCls}>
                          <option value="">Select</option>
                          <option value="SOFR">SOFR</option>
                          <option value="EURIBOR">EURIBOR</option>
                          <option value="SONIA">SONIA</option>
                          <option value="PRIME">Prime</option>
                        </select>
                      </FormField>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Spread (bps)">
                        <input type="number" value={formData.spread ?? ''} onChange={e => updateForm({ spread: e.target.value ? parseFloat(e.target.value) : undefined })} className={cn(inputCls, 'font-mono')} placeholder="350" />
                      </FormField>
                      <FormField label="Floor (%)">
                        <input type="number" step="0.01" value={formData.floor ?? ''} onChange={e => updateForm({ floor: e.target.value ? parseFloat(e.target.value) : undefined })} className={cn(inputCls, 'font-mono')} placeholder="0.50" />
                      </FormField>
                    </div>
                  </>
                )}

                {/* Fund / Alternatives */}
                {formData.instrumentType === 'FUND' && (
                  <>
                    <SectionLabel>Alternatives / Fund</SectionLabel>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField label="Vintage Year">
                        <input type="number" value={formData.vintageYear ?? ''} onChange={e => updateForm({ vintageYear: e.target.value ? parseInt(e.target.value) : undefined })} className={cn(inputCls, 'font-mono')} placeholder="2024" />
                      </FormField>
                      <FormField label="Fund Strategy">
                        <input value={formData.fundStrategy || ''} onChange={e => updateForm({ fundStrategy: e.target.value })} className={inputCls} placeholder="e.g. Buyout, Venture" />
                      </FormField>
                      <FormField label="GP Name">
                        <input value={formData.gpName || ''} onChange={e => updateForm({ gpName: e.target.value })} className={inputCls} placeholder="General Partner" />
                      </FormField>
                    </div>
                  </>
                )}
              </CustomScrollbar>

              {/* Modal Footer */}
              <div className="p-6 border-t border-white/5 bg-slate-950/30 flex gap-4">
                <button onClick={() => setShowModal(false)} className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all text-sm">Cancel</button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !formData.securityName}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-2 text-sm"
                >
                  {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {isEditing ? 'Update Security' : 'Create Security'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Container>
  );
};

// ============== Detail Tab Components ==============

const OverviewTab: React.FC<{ security: SecuritySummary; pcDetail?: PcDetailPayload | null }> = ({ security, pcDetail }) => (
  <div className="space-y-1">
    <SectionLabel>Core Identification</SectionLabel>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      <DetailField label="Asset ID" value={security.assetId || security.cusip} mono />
      <DetailField label="CUSIP" value={security.cusip} mono />
      <DetailField label="ISIN" value={security.isin} mono />
      <DetailField label="SEDOL" value={security.sedol} mono />
      <DetailField label="Security Name" value={security.securityName} />
      <DetailField label="Ticker" value={security.ticker} mono />
    </div>

    <SectionLabel>Instrument Classification</SectionLabel>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      <div>
        <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-0.5">Instrument Type</div>
        <Badge label={security.instrumentType || 'BOND'} colorMap={INSTRUMENT_COLORS} />
      </div>
      <DetailField label="Subtype" value={security.instrumentSubtype} />
      <DetailField label="Quantity Type" value={security.quantityType} />
      <DetailField label="Asset Class" value={typeof security.assetClass === 'object' ? (security.assetClass as any)?.assetClassName : security.assetClass} />
      <DetailField label="Sub-Asset Class" value={typeof security.subAssetClass === 'object' ? (security.subAssetClass as any)?.assetSubClassName : security.subAssetClass} />
    </div>

    {(security.instrumentType === 'BOND' || security.instrumentType === 'LOAN' || security.couponRate != null) && (
      <>
        <SectionLabel>Fixed Income Terms</SectionLabel>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <DetailField label="Coupon Rate" value={security.couponRate != null ? `${security.couponRate}%` : null} mono />
          <DetailField label="Coupon Frequency" value={security.couponFrequency} />
          <DetailField label="Maturity Date" value={security.maturityDate} mono />
          <DetailField label="Face Value" value={security.faceValue != null ? security.faceValue.toLocaleString() : null} mono />
          <DetailField label="Day Count" value={security.dayCountConvention} />
          <DetailField label="Currency" value={security.currency} />
        </div>
      </>
    )}

    <SectionLabel>Lifecycle & Governance</SectionLabel>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      <div>
        <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-0.5">Security Status</div>
        <Badge label={security.securityStatus || 'ACTIVE'} colorMap={STATUS_COLORS} />
      </div>
      <DetailField label="Master Status" value={security.masterStatus} />
      <DetailField label="Golden Source" value={security.goldenSource} />
      <DetailField label="Issuer" value={security.issuerName} />
      <DetailField label="Last Updated" value={security.updatedAt} mono />
    </div>

    {pcDetail?.loan && <PrivateCreditOverviewBlock detail={pcDetail} />}
  </div>
);

// ============== Private Credit Overview Block ==============
// Renders waterfall + sponsor + dates + covenants for LOAN-instrument
// securities. Sits at the bottom of the Overview tab so bond rows are
// unaffected. Driven entirely by {@link PcDetailPayload}.
const PrivateCreditOverviewBlock: React.FC<{ detail: PcDetailPayload }> = ({ detail }) => {
  const loan = detail.loan!;
  const w = loan.waterfallRecompute;

  const fmtBps = (v?: number) => (v != null ? `${v} bps` : null);
  const fmtPct = (v?: number) => (v != null ? `${v}%` : null);
  const fmtMoney = (v?: number) =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : null;

  return (
    <div className="mt-4 pt-4 border-t border-purple-500/20">
      {/* Pricing waterfall headline */}
      <SectionLabel>Pricing Waterfall</SectionLabel>
      <div className="mb-3 p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
        <div className="text-[10px] font-bold text-purple-300 uppercase tracking-widest mb-1">All-In Rate</div>
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-bold text-purple-300 font-mono">
            {loan.allInRate != null ? `${loan.allInRate.toFixed(4)}%` : '—'}
          </span>
          {w?.expression && (
            <span className="text-xs text-slate-400 font-mono">= {w.expression}</span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <DetailField label="Base Rate" value={loan.baseRate} />
        <DetailField label="Spread" value={fmtBps(loan.spreadBps)} mono />
        <DetailField label="CSA" value={fmtBps(loan.csaBps)} mono />
        <DetailField label="Floor" value={fmtPct(loan.floorRate)} mono />
        <DetailField label="Step-Down Spread" value={fmtBps(loan.stepDownSpreadBps)} mono />
        <DetailField label="Step-Down Trigger" value={loan.stepDownTriggerLeverage != null ? `< ${loan.stepDownTriggerLeverage}x` : null} mono />
        <DetailField label="OID Price" value={loan.oidPrice != null ? loan.oidPrice.toFixed(4) : null} mono />
        <DetailField label="Upfront Fee" value={fmtBps(loan.upfrontFeeBps)} mono />
      </div>

      {/* Facility + commitment */}
      <SectionLabel>Facility</SectionLabel>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <DetailField label="Loan ID" value={loan.loanId} mono />
        <DetailField label="Facility Type" value={loan.facilityType} />
        <DetailField label="Seniority" value={loan.seniority} />
        <DetailField label="Status" value={loan.status} />
        <DetailField label="Original Commitment" value={fmtMoney(loan.originalCommitment)} mono />
        <DetailField label="Current Commitment" value={fmtMoney(loan.currentCommitment)} mono />
        <DetailField label="Funded" value={fmtMoney(loan.fundedAmount)} mono />
        <DetailField label="Unfunded" value={fmtMoney(loan.unfundedAmount)} mono />
        <DetailField label="Closing Date" value={loan.closingDate} mono />
        <DetailField label="Maturity Date" value={loan.maturityDate} mono />
        <DetailField label="First Payment" value={loan.firstPaymentDate} mono />
      </div>

      {/* Sponsor + ratings */}
      <SectionLabel>Sponsor & Sector</SectionLabel>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <DetailField label="Borrower" value={loan.borrowerName} />
        <DetailField label="Borrower Ticker" value={loan.borrowerTicker} mono />
        <DetailField label="Sponsor" value={loan.sponsorName} />
        <DetailField label="Sector" value={loan.sector} />
        <DetailField label="Industry" value={loan.industry} />
        <DetailField label="Sub-Industry" value={loan.subIndustry} />
        <DetailField label="S&P" value={loan.spRating} mono />
        <DetailField label="Moody's" value={loan.moodysRating} mono />
        <DetailField label="Fitch" value={loan.fitchRating} mono />
        <DetailField label="Kroll" value={loan.krollRating} mono />
      </div>

      {/* Covenants */}
      {detail.covenants.length > 0 && (
        <>
          <SectionLabel>Covenants ({detail.covenants.length})</SectionLabel>
          <div className="space-y-2">
            {detail.covenants.map(cov => <CovenantCard key={cov.id} cov={cov} />)}
          </div>
        </>
      )}
    </div>
  );
};

const CovenantCard: React.FC<{ cov: PcCovenantSummary }> = ({ cov }) => {
  // Parse the JSON step schedule once for rendering as a mini-timeline.
  let stepRows: Array<{ date?: string; value?: number }> = [];
  if (cov.hasStepSchedule && cov.stepSchedule) {
    try {
      const parsed = JSON.parse(cov.stepSchedule);
      if (Array.isArray(parsed)) stepRows = parsed;
    } catch { /* keep stepRows empty */ }
  }

  return (
    <div className="p-3 rounded-xl bg-slate-950/50 border border-white/5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-white">{cov.covenantName || cov.metricType}</span>
          {cov.covenantType && (
            <span className="px-2 py-0.5 rounded bg-purple-500/15 text-purple-300 text-[10px] font-bold uppercase">{cov.covenantType}</span>
          )}
          {cov.covenantCategory && (
            <span className="px-2 py-0.5 rounded bg-slate-700/40 text-slate-300 text-[10px] uppercase">{cov.covenantCategory}</span>
          )}
        </div>
        {cov.isActive === false && (
          <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-400 text-[10px] font-bold uppercase">Inactive</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-0.5">Threshold</div>
          <div className="text-white font-mono">
            {cov.thresholdType === 'MAX' ? '≤ ' : cov.thresholdType === 'MIN' ? '≥ ' : ''}
            {cov.thresholdValue != null ? cov.thresholdValue : '—'}
            {cov.thresholdValueMax != null ? ` – ${cov.thresholdValueMax}` : ''}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-0.5">Measurement</div>
          <div className="text-slate-300">
            {cov.measurementFrequency || '—'}
            {cov.measurementPeriod ? ` / ${cov.measurementPeriod}` : ''}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-0.5">Equity Cure</div>
          <div className="text-slate-300">
            {cov.hasEquityCure
              ? `Yes (max ${cov.maxEquityCures ?? '—'})`
              : 'No'}
          </div>
        </div>
      </div>
      {stepRows.length > 0 && (
        <div className="mt-3 pt-2 border-t border-white/5">
          <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1.5">Step Schedule</div>
          <div className="flex flex-wrap gap-2">
            {stepRows.map((s, i) => (
              <span key={i} className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[10px] font-mono">
                {s.date || '—'} → {s.value != null ? s.value : '—'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const IdentifiersTab: React.FC<{ identifiers: Identifier[]; securityId: number; onRefresh: () => void }> = ({ identifiers, securityId, onRefresh }) => {
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState('');
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddIdentifier = async () => {
    if (!newType || !newValue) return;
    setSaving(true);
    try {
      await axios.post(`/api/security-master/${securityId}/identifiers`, { identifierType: newType, identifierValue: newValue, isPrimary: false });
      setAdding(false);
      setNewType('');
      setNewValue('');
      onRefresh();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <SectionLabel>Security Identifiers</SectionLabel>
        <button onClick={() => setAdding(!adding)} className="text-[11px] font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1">
          <PlusCircle className="w-3 h-3" /> Add
        </button>
      </div>
      {adding && (
        <div className="flex gap-2 items-end p-3 bg-slate-950/50 rounded-xl border border-white/5">
          <FormField label="Type">
            <select value={newType} onChange={e => setNewType(e.target.value)} className={cn(selectCls, 'text-xs py-2')}>
              <option value="">Select</option>
              <option value="CUSIP">CUSIP</option>
              <option value="ISIN">ISIN</option>
              <option value="SEDOL">SEDOL</option>
              <option value="TICKER">Ticker</option>
              <option value="FIGI">FIGI</option>
              <option value="RIC">RIC</option>
              <option value="INTERNAL">Internal</option>
            </select>
          </FormField>
          <FormField label="Value">
            <input value={newValue} onChange={e => setNewValue(e.target.value)} className={cn(inputCls, 'font-mono text-xs py-2')} placeholder="Identifier value" />
          </FormField>
          <button onClick={handleAddIdentifier} disabled={saving} className="px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg">
            {saving ? '...' : 'Save'}
          </button>
        </div>
      )}
      <MiniTable
        columns={[
          { key: 'identifierType', label: 'Type', width: '80px' },
          { key: 'identifierValue', label: 'Value', mono: true },
          { key: 'isPrimary', label: 'Primary', width: '70px' },
          { key: 'status', label: 'Status', width: '70px' },
          { key: 'source', label: 'Source', width: '80px' },
          { key: 'effectiveDate', label: 'Effective', width: '100px', mono: true },
        ]}
        rows={identifiers.map(id => ({
          ...id,
          isPrimary: id.isPrimary ? 'Yes' : 'No',
        }))}
        emptyText="No identifiers loaded"
      />
    </div>
  );
};

const RatingsTab: React.FC<{ ratings: Rating[]; securityId: number; onRefresh: () => void }> = ({ ratings, securityId, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [agency, setAgency] = useState('S&P');
  const [rating, setRating] = useState('');
  const [outlook, setOutlook] = useState('STABLE');
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-slate-900/80 border border-white/10 text-white text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500/50";

  const handleSave = async () => {
    if (!rating.trim()) return;
    setSaving(true);
    try {
      await axios.post(`/api/security-master/${securityId}/ratings`, {
        ratingAgency: agency, rating: rating.trim(), ratingOutlook: outlook,
        ratingType: 'ISSUE', asOfDate: getTenantToday(),
        ratingDate: getTenantToday(), effectiveDate: getTenantToday(),
      });
      setShowAdd(false); setRating(''); onRefresh();
    } catch (e) { console.error('Failed to add rating', e); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <SectionLabel>Credit Ratings</SectionLabel>
        <button onClick={() => setShowAdd(!showAdd)} className="px-3 py-1.5 text-[10px] font-bold rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 transition-all">
          <PlusCircle className="w-3 h-3 inline mr-1" />{showAdd ? 'Cancel' : 'Add Rating'}
        </button>
      </div>
      {showAdd && (
        <div className="p-3 bg-slate-950/70 rounded-xl border border-amber-500/20 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="text-[9px] text-slate-500 uppercase mb-1">Agency</div>
              <select value={agency} onChange={e => setAgency(e.target.value)} className={inputCls}>
                {["S&P","MOODY","FITCH","DBRS","KBRA"].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[9px] text-slate-500 uppercase mb-1">Rating</div>
              <input value={rating} onChange={e => setRating(e.target.value)} className={inputCls} placeholder="e.g. AA+" />
            </div>
            <div>
              <div className="text-[9px] text-slate-500 uppercase mb-1">Outlook</div>
              <select value={outlook} onChange={e => setOutlook(e.target.value)} className={inputCls}>
                {["STABLE","POSITIVE","NEGATIVE","WATCH_POSITIVE","WATCH_NEGATIVE"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving || !rating.trim()} className="w-full py-2 bg-amber-600/80 hover:bg-amber-600 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-all">
            {saving ? 'Saving...' : 'Save Rating'}
          </button>
        </div>
      )}
      {ratings.length === 0 && !showAdd ? (
        <div className="py-10 text-center text-slate-600 text-xs">No ratings available</div>
      ) : (
        <div className="space-y-2">
          {ratings.map((r, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-slate-950/50 rounded-xl border border-white/5">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                <Award className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-white">{r.ratingAgency || r.agency}</span>
                  <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 text-xs font-bold font-mono">{r.rating}</span>
                  {(r.ratingOutlook || r.outlook) && <span className="text-[10px] text-slate-500 uppercase">{r.ratingOutlook || r.outlook}</span>}
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                  Effective: {r.effectiveDate}{r.expirationDate ? ` | Expires: ${r.expirationDate}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SchedulesTab: React.FC<{
  coupons: CouponSchedule[];
  putCall: PutCallSchedule[];
  amortization: AmortizationSchedule[];
  subTab: string;
  setSubTab: (t: 'coupons' | 'putcall' | 'amortization') => void;
}> = ({ coupons, putCall, amortization, subTab, setSubTab }) => (
  <div className="space-y-3">
    <div className="flex gap-1 bg-slate-950/50 rounded-lg p-1 border border-white/5">
      {[
        { id: 'coupons' as const, label: 'Coupons' },
        { id: 'putcall' as const, label: 'Put/Call' },
        { id: 'amortization' as const, label: 'Amortization' },
      ].map(t => (
        <button
          key={t.id}
          onClick={() => setSubTab(t.id)}
          className={cn(
            'flex-1 py-1.5 text-[11px] font-bold rounded-md transition-all',
            subTab === t.id ? 'bg-blue-600/20 text-blue-400' : 'text-slate-500 hover:text-slate-300'
          )}
        >
          {t.label}
        </button>
      ))}
    </div>

    {subTab === 'coupons' && (
      <MiniTable
        columns={[
          { key: 'paymentDate', label: 'Payment Date', mono: true },
          { key: 'couponRate', label: 'Rate', mono: true },
          { key: 'amount', label: 'Amount', mono: true },
          { key: 'status', label: 'Status' },
        ]}
        rows={coupons}
        emptyText="No coupon schedule"
      />
    )}
    {subTab === 'putcall' && (
      <MiniTable
        columns={[
          { key: 'type', label: 'Type' },
          { key: 'date', label: 'Date', mono: true },
          { key: 'price', label: 'Price', mono: true },
          { key: 'condition', label: 'Condition' },
        ]}
        rows={putCall}
        emptyText="No put/call schedule"
      />
    )}
    {subTab === 'amortization' && (
      <MiniTable
        columns={[
          { key: 'date', label: 'Date', mono: true },
          { key: 'principal', label: 'Principal', mono: true },
          { key: 'factor', label: 'Factor', mono: true },
          { key: 'balance', label: 'Balance', mono: true },
        ]}
        rows={amortization}
        emptyText="No amortization schedule"
      />
    )}
  </div>
);

const RelationshipsTab: React.FC<{ relationships: RelatedSecurity[]; lenders?: PcLenderSummary[] }> = ({ relationships, lenders = [] }) => {
  // Role chip colors: admin agent is the most prominent (yellow), lead arrangers
  // blue, plain lenders muted, support roles slate. Keeps the syndicate hierarchy
  // legible at a glance for the demo walk-through.
  const roleColors: Record<string, { bg: string; text: string; border: string }> = {
    ADMIN_AGENT:        { bg: 'rgba(250,204,21,0.15)', text: '#facc15', border: 'rgba(250,204,21,0.3)' },
    LEAD_ARRANGER:      { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
    SYNDICATION_AGENT:  { bg: 'rgba(168,85,247,0.15)', text: '#c084fc', border: 'rgba(168,85,247,0.3)' },
    DOCUMENTATION_AGENT:{ bg: 'rgba(148,163,184,0.15)', text: '#cbd5e1', border: 'rgba(148,163,184,0.3)' },
    COLLATERAL_AGENT:   { bg: 'rgba(148,163,184,0.15)', text: '#cbd5e1', border: 'rgba(148,163,184,0.3)' },
    LENDER:             { bg: 'rgba(100,116,139,0.15)', text: '#94a3b8', border: 'rgba(100,116,139,0.3)' },
  };

  return (
    <div className="space-y-5">
      {lenders.length > 0 && (
        <div>
          <SectionLabel>Lender Register / Syndicate</SectionLabel>
          <div className="rounded-xl bg-slate-950/50 border border-white/5 overflow-hidden">
            <div className="grid grid-cols-[2fr_1.2fr_1fr_0.8fr] gap-3 px-3 py-2 bg-slate-950/70 border-b border-white/5 text-[9px] font-bold text-slate-500 uppercase tracking-widest">
              <div>Lender</div>
              <div>Role</div>
              <div>Commitment</div>
              <div className="text-right">Pro-Rata</div>
            </div>
            {lenders.map(lender => {
              const palette = roleColors[lender.lenderRole || 'LENDER'] || roleColors.LENDER;
              return (
                <div key={lender.id} className="grid grid-cols-[2fr_1.2fr_1fr_0.8fr] gap-3 px-3 py-2.5 border-b border-white/5 last:border-b-0 text-xs">
                  <div className="text-white font-medium truncate">{lender.lenderName}</div>
                  <div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: palette.bg, color: palette.text, border: `1px solid ${palette.border}` }}>
                      {lender.lenderRole || 'LENDER'}
                    </span>
                  </div>
                  <div className="text-slate-300 font-mono">
                    {lender.commitmentAmount != null
                      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(lender.commitmentAmount)
                      : '—'}
                  </div>
                  <div className="text-right text-slate-300 font-mono">
                    {lender.proRataPct != null ? `${lender.proRataPct}%` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <SectionLabel>Related Securities</SectionLabel>
        {relationships.length === 0 ? (
          <div className="py-10 text-center text-slate-600 text-xs">No related securities</div>
        ) : (
          relationships.map((rel, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-slate-950/50 rounded-xl border border-white/5 mb-2">
              <div className="w-9 h-9 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                <Link2 className="w-4 h-4 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{rel.relatedSecurityName}</div>
                <div className="text-[10px] text-slate-500">{rel.description || `ID: ${rel.relatedSecurityId}`}</div>
              </div>
              <span className="px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase">{rel.relationshipType}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const EnhancementsTab: React.FC<{ enhancements: Enhancement[]; classifications: Classification[]; securityId: number; onRefresh: () => void }> = ({ enhancements, classifications, securityId, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [enhType, setEnhType] = useState('INSURANCE_WRAP');
  const [provider, setProvider] = useState('');
  const [coveragePct, setCoveragePct] = useState('');
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full bg-slate-900/80 border border-white/10 text-white text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500/50";

  const handleSave = async () => {
    if (!provider.trim()) return;
    setSaving(true);
    try {
      await axios.post(`/api/security-master/${securityId}/enhancements`, {
        enhancementType: enhType, providerName: provider.trim(),
        coveragePct: coveragePct ? parseFloat(coveragePct) : null,
        effectiveDate: getTenantToday(), status: 'ACTIVE',
      });
      setShowAdd(false); setProvider(''); setCoveragePct(''); onRefresh();
    } catch (e) { console.error('Failed to add enhancement', e); }
    finally { setSaving(false); }
  };

  return (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <SectionLabel>Credit Enhancements</SectionLabel>
      <button onClick={() => setShowAdd(!showAdd)} className="px-3 py-1.5 text-[10px] font-bold rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 transition-all">
        <PlusCircle className="w-3 h-3 inline mr-1" />{showAdd ? 'Cancel' : 'Add Enhancement'}
      </button>
    </div>
    {showAdd && (
      <div className="p-3 bg-slate-950/70 rounded-xl border border-emerald-500/20 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className="text-[9px] text-slate-500 uppercase mb-1">Type</div>
            <select value={enhType} onChange={e => setEnhType(e.target.value)} className={inputCls}>
              {["INSURANCE_WRAP","LETTER_OF_CREDIT","GOVT_GUARANTEE","OVERCOLLATERALIZATION","RESERVE_FUND","SUBORDINATION","SURETY_BOND"].map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[9px] text-slate-500 uppercase mb-1">Provider</div>
            <input value={provider} onChange={e => setProvider(e.target.value)} className={inputCls} placeholder="e.g. Assured Guaranty" />
          </div>
          <div>
            <div className="text-[9px] text-slate-500 uppercase mb-1">Coverage %</div>
            <input value={coveragePct} onChange={e => setCoveragePct(e.target.value)} className={inputCls} placeholder="e.g. 100" type="number" />
          </div>
        </div>
        <button onClick={handleSave} disabled={saving || !provider.trim()} className="w-full py-2 bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-all">
          {saving ? 'Saving...' : 'Save Enhancement'}
        </button>
      </div>
    )}
    {enhancements.length === 0 && !showAdd ? (
      <div className="py-6 text-center text-slate-600 text-xs">No enhancements</div>
    ) : (
      <MiniTable
        columns={[
          { key: 'enhancementType', label: 'Type' },
          { key: 'providerName', label: 'Provider' },
          { key: 'coverageAmount', label: 'Amount', mono: true },
          { key: 'coveragePct', label: 'Coverage %', mono: true },
          { key: 'status', label: 'Status' },
          { key: 'expiryDate', label: 'Expires', mono: true },
        ]}
        rows={enhancements}
      />
    )}

    {classifications.length > 0 && (
      <>
        <SectionLabel>Classifications</SectionLabel>
        <MiniTable
          columns={[
            { key: 'scheme', label: 'Scheme' },
            { key: 'category', label: 'Category' },
            { key: 'subcategory', label: 'Subcategory' },
          ]}
          rows={classifications}
        />
      </>
    )}
  </div>
  );
};

const LiquidityTab: React.FC<{ data: LiquiditySnapshot | null }> = ({ data }) => {
  if (!data) return <div className="py-10 text-center text-slate-600 text-xs">No liquidity data available</div>;

  const tierColor = data.tier === 'TIER_1' ? '#10b981' : data.tier === 'TIER_2' ? '#f59e0b' : '#ef4444';

  return (
    <div className="space-y-4">
      <SectionLabel>Liquidity Snapshot</SectionLabel>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
          <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Liquidity Tier</div>
          <span className="px-3 py-1 rounded-lg text-sm font-bold" style={{ background: `${tierColor}18`, color: tierColor }}>{data.tier || '--'}</span>
        </div>
        <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
          <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Avg Daily Volume</div>
          <div className="text-lg font-bold text-white font-mono">{data.averageDailyVolume != null ? `$${(data.averageDailyVolume / 1_000_000).toFixed(1)}M` : '--'}</div>
        </div>
        <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
          <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Bid-Ask Spread</div>
          <div className="text-lg font-bold text-white font-mono">{data.bidAskSpread != null ? `${data.bidAskSpread.toFixed(2)} bps` : '--'}</div>
        </div>
        <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
          <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Venue Availability</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {data.venueAvailability?.length ? data.venueAvailability.map(v => (
              <span key={v} className="px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 text-[10px] font-bold border border-cyan-500/20">{v}</span>
            )) : <span className="text-slate-600 text-xs">--</span>}
          </div>
        </div>
      </div>
      {data.lastUpdated && (
        <div className="text-[10px] text-slate-600 text-right font-mono">Last updated: {data.lastUpdated}</div>
      )}
    </div>
  );
};

const AuditTrailTab: React.FC<{ entries: AuditEntry[] }> = ({ entries }) => (
  <div className="space-y-3">
    <SectionLabel>Change History</SectionLabel>
    {entries.length === 0 ? (
      <div className="py-10 text-center text-slate-600 text-xs">No audit trail entries</div>
    ) : (
      <div className="space-y-2">
        {entries.map((entry, i) => (
          <div key={i} className="p-3 bg-slate-950/50 rounded-xl border border-white/5">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center border border-white/5">
                  <History className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <div>
                  <span className="text-xs font-bold text-white">{entry.action}</span>
                  {entry.field && <span className="text-[10px] text-slate-500 ml-2">Field: {entry.field}</span>}
                </div>
              </div>
              <span className="text-[10px] text-slate-600 font-mono">{entry.changedAt}</span>
            </div>
            {(entry.oldValue || entry.newValue) && (
              <div className="mt-2 ml-9 flex gap-3 text-xs">
                {entry.oldValue && (
                  <div className="flex items-center gap-1">
                    <span className="text-red-400/60 line-through font-mono">{entry.oldValue}</span>
                  </div>
                )}
                {entry.oldValue && entry.newValue && <ChevronRight className="w-3 h-3 text-slate-600" />}
                {entry.newValue && (
                  <span className="text-emerald-400 font-mono">{entry.newValue}</span>
                )}
              </div>
            )}
            <div className="mt-1 ml-9 text-[10px] text-slate-600">by {entry.changedBy}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default SecurityManagementScreen;
